import { lazy, useEffect, useState } from 'react';
import { BlockWrapper, defineBlock, smartProps, useInspectorControl } from '@smartfire/js-block-engine';
import { EditableHeading } from '@smartfire/components';
import { Tabs, Tab } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Scrollbar } from 'swiper/modules';
import { useProducts } from '../../shared/hooks/useProducts';
import { __ } from '../../shared/tools';
import { LoadingTile, ProductTile } from '../product-grid/product-grid-tile';
import ArrowLeft from '../../assets/icons/arrow_carrousel_left';
import ArrowRight from '../../assets/icons/arrow_carrousel_right';
import clsx from 'clsx';

import 'swiper/css';
import 'swiper/css/scrollbar';
import style from './style.module.scss';

const Inspector = lazy(() => import('./inspector'));

const HomeSliderProducts = ({ activeLang, categories = [], per_page = 5, backgroundColor }) => {
    useInspectorControl(Inspector);
    const [currentCategory, setCurrentCategory] = useState(null);

    useEffect(() => {
        categories && categories.length > 0 && setCurrentCategory(categories[0].id);
    }, [categories]);

    const handleChange = (event, val) => {
        setCurrentCategory(val);
    };

    return (
        <BlockWrapper.Section className={style.block} style={{ backgroundColor }}>
            <div className={style.content}>
                <EditableHeading smartProp="title" className={style.title} placeholder="Découvrez..." />
                <div className={style.header}>
                    {categories.length > 0 && (
                        <Tabs
                            value={currentCategory ?? categories[0].id}
                            onChange={handleChange}
                            aria-label=""
                            className={style.categories}
                        >
                            {categories.map((category) => (
                                <Tab
                                    key={category.id}
                                    label={category.name}
                                    id={category.id}
                                    value={category.id}
                                    className={clsx(style.category, currentCategory === category.id && style.active)}
                                />
                            ))}
                        </Tabs>
                    )}
                    <div className={style.navigation}>
                        <ArrowLeft
                            className={clsx(style.arrow, style.prev, 'home-products-swiper-button-prev')}
                            role="button"
                        />
                        <ArrowRight
                            className={clsx(style.arrow, style.next, 'home-products-swiper-button-next')}
                            role="button"
                        />
                    </div>
                </div>
                <ProductsList activeLang={activeLang} category={currentCategory} per_page={per_page} />
            </div>
        </BlockWrapper.Section>
    );
};

const ProductsList = ({ activeLang, category = null, per_page = 5 }) => {
    const { productsLoading, products } = useProducts({
        category: category !== null ? category : undefined,
        lang: activeLang ?? 'fr',
        per_page: per_page,
        featured: 1,
    });

    const swiperProps = {
        spaceBetween: 65,
        slidesPerView: 1.3,
        className: style.swiper,
        autoplay: { delay: 3000, pauseOnMouseEnter: true },
        speed: 600,
        navigation: {
            nextEl: '.home-products-swiper-button-next',
            prevEl: '.home-products-swiper-button-prev',
            disabledClass: style.disabled,
            hiddenClass: style.disabled,
        },
        scrollbar: {
            draggable: true,
        },
        breakpoints: {
            767: {
                slidesPerView: 3,
            },
        },
        modules: [Autoplay, Navigation, Scrollbar],
    };

    return productsLoading || products.length > 0 ? (
        <div className={style.carrousel}>
            <Swiper {...swiperProps}>
                {(productsLoading ? Array.from(new Array(3)) : products).map((product, i) => {
                    return product ? (
                        <SwiperSlide className={style.slide} key={product.id}>
                            <ProductTile
                                product={product}
                                buttons={{ discover: true }}
                                showTags={false}
                                className={style.tile}
                            />
                        </SwiperSlide>
                    ) : (
                        <SwiperSlide className={style.slide} key={i}>
                            <LoadingTile key={i} className={style.tile} />
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </div>
    ) : (
        <p className={style.empty}>{__('Aucun résultat')}</p>
    );
};

export default defineBlock({
    Block: HomeSliderProducts,
    setup: {
        title: 'Slider Produits',
    },
    smartProps: {
        activeLang: smartProps.string.contentServerSide(),
        title: smartProps.object.withDefault({
            headingLevel: 2,
            content: '',
        }),
        categories_ids: smartProps.array.withDefault([]),
        categories: smartProps.array.contentServerSide(),
        per_page: smartProps.number.withDefault(5),
        backgroundColor: smartProps.string.withDefault(''),
    },
});
